import React, { useEffect, useState, useRef, useContext, useMemo, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import '../styles/navbar.css';
import config from '../config';
import axios from 'axios';
import Notification from './Notification';
import SearchSuggestions from './SearchSuggestions';
import { SideMenuCategorySkeleton } from './Skeletons';
import logo from '../assets/wennze.png';
import Cookies from 'js-cookie';
import { UserContext } from '../contexts/UserContext';
import { CategoryContext } from '../contexts/CategoryContext';
import { NOTIFICATION_READ_EVENT } from '../pages/UserNotifications';
import AppDownloadButton from './AppDownloadButton';

const Navbar = ({ onLoginClick, onRegisterClick, onChangePasswordClick }) => {
    const { user, logout } = useContext(UserContext);
    const {
        categories,
        topCategories,
        categoryList,
        fetchSubcategories,
        subcategoriesMap,
        isServerDown,
        isLoading: categoriesLoading
    } = useContext(CategoryContext);
    const isLoggedIn = !!user;
    
    // Category icon mapping for better visual representation
    const getCategoryIcon = (categoryName) => {
        const name = categoryName?.toLowerCase() || '';
        const iconMap = {
            // Technology & Electronics
            'électronique': 'fas fa-laptop',
            'electronic': 'fas fa-laptop',
            'electronics': 'fas fa-laptop',
            'téléphones': 'fas fa-mobile-alt',
            'phones': 'fas fa-mobile-alt',
            'ordinateurs': 'fas fa-desktop',
            'computers': 'fas fa-desktop',
            'accessoires': 'fas fa-plug',
            'accessories': 'fas fa-plug',
            
            // Fashion & Clothing
            'vêtements': 'fas fa-tshirt',
            'clothes': 'fas fa-tshirt',
            'fashion': 'fas fa-tshirt',
            'mode': 'fas fa-tshirt',
            'chaussures': 'fas fa-shoe-prints',
            'shoes': 'fas fa-shoe-prints',
            'bijoux': 'fas fa-gem',
            'jewelry': 'fas fa-gem',
            
            // Home & Living
            'maison': 'fas fa-home',
            'home': 'fas fa-home',
            'meubles': 'fas fa-couch',
            'furniture': 'fas fa-couch',
            'décoration': 'fas fa-paint-brush',
            'decoration': 'fas fa-paint-brush',
            'jardin': 'fas fa-leaf',
            'garden': 'fas fa-leaf',
            
            // Vehicles
            'voitures': 'fas fa-car',
            'cars': 'fas fa-car',
            'véhicules': 'fas fa-car',
            'vehicles': 'fas fa-car',
            'motos': 'fas fa-motorcycle',
            'motorcycles': 'fas fa-motorcycle',
            
            // Sports & Hobbies
            'sports': 'fas fa-football-ball',
            'sport': 'fas fa-football-ball',
            'fitness': 'fas fa-dumbbell',
            'loisirs': 'fas fa-gamepad',
            'hobbies': 'fas fa-gamepad',
            
            // Beauty & Health
            'beauté': 'fas fa-spray-can',
            'beauty': 'fas fa-spray-can',
            'santé': 'fas fa-heartbeat',
            'health': 'fas fa-heartbeat',
            
            // Food & Beverage
            'alimentation': 'fas fa-utensils',
            'food': 'fas fa-utensils',
            'boissons': 'fas fa-wine-bottle',
            'drinks': 'fas fa-wine-bottle',
            
            // Books & Media
            'livres': 'fas fa-book',
            'books': 'fas fa-book',
            'musique': 'fas fa-music',
            'music': 'fas fa-music',
            'films': 'fas fa-film',
            'movies': 'fas fa-film',
            
            // Services
            'services': 'fas fa-handshake',
            'service': 'fas fa-handshake',
            
            // Animals & Pets
            'animaux': 'fas fa-paw',
            'animals': 'fas fa-paw',
            'pets': 'fas fa-paw',
            
            // Default
            'default': 'fas fa-tag'
        };
        
        // Try to find a matching icon
        for (const [key, icon] of Object.entries(iconMap)) {
            if (name.includes(key)) {
                return icon;
            }
        }
        
        return iconMap['default'];
    };
    
    const [userFirstName, setUserFirstName] = useState('');
    const [userNewMessages, setUserNewMessages] = useState('');
    const [userProducts, setUserProducts] = useState('');
    const [userLastName, setUserLastName] = useState('');
    const [userName, setUserName] = useState('');
    const [userProfilePictureUrl, setUserProfilePictureUrl] = useState(null); // Update state to store profile picture URL
    const [userId, setUserId] = useState(null); // State to store user ID
    const [userUuid, setUserUuid] = useState(null); // State to store user UUID for navigation
    const [subcategories, setSubcategories] = useState([]);
    const [activeCategory, setActiveCategory] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [isMenuOpen, setIsMenuOpen] = useState(false); // State for hamburger menu
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false); // State for profile menu
    const [notifications, setNotifications] = useState([]); // State for notifications
    const [unreadCount, setUnreadCount] = useState(0); // State for unread notification count
    const [isNotificationDropdownOpen, setIsNotificationDropdownOpen] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [isSideMenuOpen, setIsSideMenuOpen] = useState(false); // State for side menu
    const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false); // State for category dropdown
    const location = useLocation(); // Hook to get the current location
    const navigate = useNavigate();
    const [recentSearches, setRecentSearches] = useState([]);
    const [showRecentSearches, setShowRecentSearches] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
    const [notification, setNotification] = useState({ message: '', type: 'info', header: '' }); // Add notification state
    const [showDeleteWarning, setShowDeleteWarning] = useState(false); // Delete account warning modal
    const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
    const debounceTimerRef = useRef(null);
    const searchInputRef = useRef(null);

    useEffect(() => {
        if (user) {
            // Update local state from user context
            setUserFirstName(user.first_name);
            setUserLastName(user.last_name);
            setUserName(user.username);
            // Handle both profile_picture_url and profile_picture for API compatibility
            setUserProfilePictureUrl(user.profile_picture_url || user.profile_picture);
            setUserId(user.id);
            setUserUuid(user.uuid || user.id);  // Store UUID, fallback to ID
            setUserNewMessages(Number(user.new_messages));
            setUserProducts(user.products);
        }
    }, [user]);

    useEffect(() => {
        const fetchNotifications = async () => {
            try {
                const token = Cookies.get('authToken'); // Retrieve token from cookies
                const response = await fetch(`${config.backendUrl}/notifications/`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Token ${token}`
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    setNotifications(data.results); // Update to use data.results
                } else {
                    console.error('Failed to fetch notifications');
                }
            } catch (error) {
                console.error('An error occurred while fetching notifications:', error);
            }
        };
        
        const fetchUnreadCount = async () => {
            try {
                const token = Cookies.get('authToken');
                const response = await fetch(`${config.backendUrl}/notifications/unread-count/`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Token ${token}`
                    }
                });
                
                if (response.ok) {
                    const data = await response.json();
                    setUnreadCount(data.unread_count); // Fixed: use unread_count instead of count
                } else {
                    // Fallback to calculating from all notifications
                    const notificationsResponse = await fetch(`${config.backendUrl}/notifications/`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Token ${token}`
                        }
                    });
                    
                    if (notificationsResponse.ok) {
                        const notificationsData = await notificationsResponse.json();
                        // All notifications are unread (read ones are deleted)
                        setUnreadCount(notificationsData.results.length);
                    }
                }
            } catch (error) {
                console.error('Error fetching unread notification count:', error);
            }
        };

        if (isLoggedIn) {
            fetchNotifications();
            fetchUnreadCount();
        }
    }, [isLoggedIn]);

    useEffect(() => {
        if (isLoggedIn && userId) {
            const token = Cookies.get('authToken');
            const socket = new WebSocket(`${config.websocketUrl}notifications/${userId}/?token=${token}`);

            socket.onmessage = (event) => {
                const data = JSON.parse(event.data);
                
                // Handle new notification
                if (data.notification) {
                    setNotifications((prevNotifications) => [data.notification, ...prevNotifications]);
                    setUnreadCount((prevCount) => prevCount + 1);
                }
                // Handle notification update (bulk delete, delete all)
                else if (data.type === 'notification_update') {
                    setUnreadCount(data.unread_count);
                }
            };

            // Listen for notification read events from other components
            const handleNotificationReadChanged = (e) => {
                if (e.detail && e.detail.notificationId) {
                    setNotifications(prevNotifications =>
                        prevNotifications.map(notification =>
                            notification.id === e.detail.notificationId
                                ? { ...notification, is_read: true }
                                : notification
                        )
                    );
                    setUnreadCount(prevCount => Math.max(prevCount - 1, 0));
                }
            };

            window.addEventListener(NOTIFICATION_READ_EVENT, handleNotificationReadChanged);

            return () => {
                socket.close();
                window.removeEventListener(NOTIFICATION_READ_EVENT, handleNotificationReadChanged);
            };
        }
    }, [isLoggedIn, userId]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            // More comprehensive outside click detection
            const isClickInsideProfileMenu = event.target.closest('.profile-menu');
            const isClickInsideCategoryMenu = event.target.closest('.category-menu');
            const isClickInsideNotificationBell = event.target.closest('.notification-bell');
            const isClickInsideRecentSearches = event.target.closest('.recent-searches');
            const isClickInsideSuggestions = event.target.closest('.search-suggestions');
            const isClickInsideSubmenu = event.target.closest('.navbar-submenu');
            
            // Close menus only if clicking outside all dropdown areas and submenus
            if (!isClickInsideProfileMenu && !isClickInsideCategoryMenu && !isClickInsideNotificationBell && !isClickInsideRecentSearches && !isClickInsideSuggestions && !isClickInsideSubmenu) {
                closeAllMenus();
                setShowRecentSearches(false);
                setShowSuggestions(false);
            }
        };

        const handleTouchOutside = (event) => {
            // Handle touch events for mobile devices - more specific for mobile
            const isClickInsideProfileMenu = event.target.closest('.profile-menu');
            const isClickInsideCategoryMenu = event.target.closest('.category-menu');
            const isClickInsideNotificationBell = event.target.closest('.notification-bell');
            const isClickInsideRecentSearches = event.target.closest('.recent-searches');
            const isClickInsideSuggestions = event.target.closest('.search-suggestions');
            const isClickInsideSubmenu = event.target.closest('.navbar-submenu');
            
            // For mobile, be more aggressive about closing dropdowns
            // Also check if the click is on the overlay/backdrop
            const isClickOnOverlay = event.target.classList.contains('profile-menu') && 
                                   event.target.classList.contains('active') &&
                                   !event.target.closest('.navbar-submenu');
            
            if (isClickOnOverlay || (!isClickInsideSubmenu && (isProfileMenuOpen || isNotificationDropdownOpen))) {
                closeAllMenus();
                setShowRecentSearches(false);
                setShowSuggestions(false);
            } else if (!isClickInsideProfileMenu && !isClickInsideCategoryMenu && !isClickInsideNotificationBell && !isClickInsideRecentSearches && !isClickInsideSuggestions) {
                closeAllMenus();
                setShowRecentSearches(false);
                setShowSuggestions(false);
            }
        };

        // Add both mouse and touch event listeners for better mobile support
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleTouchOutside);
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleTouchOutside);
        };
    }, [isProfileMenuOpen, isNotificationDropdownOpen]);
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= 480) {
                setIsMenuOpen(false); // Close the menu on small screens
            }
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // Call once to set initial state

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const handleLogout = async () => {
        setIsLoggingOut(true); // Start loading
        try {
            const token = Cookies.get('authToken');
            const currentPath = location.pathname;
            const protectedRoutes = [
                '/profile',
                '/add-post',
                '/messages',
                '/likes',
                '/user/likes',
                '/products/user',
                '/user/corner',
                '/portefeuille',
                '/communication',
                '/sms-history',
                '/delete-account',
                '/delete-account-verification',
                '/user/notifications',
                '/user/products',

            ];

            // Use the logout function from UserContext instead of direct API call
            await logout(); // Add this line and remove the fetch call

            // Clear all local state
            setUserFirstName('');
            setUserLastName('');
            setUserName('');
            setUserProfilePictureUrl(null);
            setUserId(null);
            setUserNewMessages('');
            setUserProducts('');
            setNotifications([]);
            setUnreadCount(0);

            // Check if current path is a protected route
            const isProtectedRoute = protectedRoutes.some(route =>
                currentPath.startsWith(route)
            );

            if (isProtectedRoute) {
                navigate('/dashboard');
                setNotification({
                    message: 'Connectez-vous pour accéder à cette page',
                    type: 'info',
                    header: 'Information'
                });
            } else {
                setNotification({
                    message: 'Vous êtes déconnecté',
                    type: 'success',
                    header: 'Succès'
                });
            }
        } catch (error) {
            console.error('Une erreur est survenue lors de la déconnexion:', error);
            setNotification({
                message: 'Une erreur est survenue lors de la déconnexion',
                type: 'error',
                header: 'Erreur'
            });
        } finally {
            setIsLoggingOut(false); // Stop loading
        }
    };

    const handleDeleteAccountClick = () => {
        setShowDeleteWarning(true);
        closeAllMenus();
    };

    const handleConfirmDelete = () => {
        setShowDeleteWarning(false);
        navigate('/delete-account');
    };

    const handleCancelDelete = () => {
        setShowDeleteWarning(false);
    };

    const handleCategoryClick = async (categoryId) => {
        try {
            // Set the active category to the clicked category
            setActiveCategory(categoryId);

            // Check if subcategories are already cached
            if (subcategoriesMap[categoryId]) {

                setSubcategories(subcategoriesMap[categoryId]);
                return;
            }

            // Fetch subcategories if not cached

            const subcategories = await fetchSubcategories(categoryId);
            setSubcategories(subcategories || []);
        } catch (error) {
            console.error('Error handling category click:', error);
            setSubcategories([]); // Set empty array on error
        }
    };
    // Memoize processed categories
    const processedCategories = useMemo(() => {
        return categories.map(category => ({
            ...category,
            subcategories: subcategoriesMap[category.id] || []
        }));
    }, [categories, subcategoriesMap]);

    useEffect(() => {
        if (isServerDown) {

            return; // Exit early if the server is down
        }

        if (categories.length > 0) {
            const fetchAllSubcategories = async () => {
                try {
                    await Promise.allSettled(
                        categories.map(async (category) => {
                            if (!subcategoriesMap[category.id]) {
                                await fetchSubcategories(category.id);
                            }
                        })
                    );
                } catch (error) {
                    console.error('Error fetching subcategories:', error);
                }
            };

            fetchAllSubcategories();
        }
    }, [categories, subcategoriesMap, isServerDown, fetchSubcategories]);

    const closeNotificationDropdown = () => {
        setIsNotificationDropdownOpen(false);
    };

    const handleNotificationClick = async (notificationId, targetUrl) => {
        try {
            // Close dropdown immediately for better UX
            closeAllMenus();
            
            const token = Cookies.get('authToken'); // Retrieve token from cookies
            
            // CRITICAL FIX: Guard against unauthenticated users clicking notifications
            if (!token) {

                // Still navigate to target URL even if not authenticated
                if (targetUrl) {
                    let processedUrl = targetUrl;
                    if (processedUrl.includes(window.location.origin)) {
                        processedUrl = processedUrl.replace(window.location.origin, '');
                    }
                    if (!processedUrl.startsWith('/')) {
                        processedUrl = '/' + processedUrl;
                    }
                    processedUrl = processedUrl.replace('/api/', '/');
                    navigate(processedUrl);
                }
                return;
            }
            
            // Delete the notification instead of marking as read
            const response = await fetch(`${config.backendUrl}/notifications/${notificationId}/read/`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${token}`,
                },
            });

            if (response.ok) {
                const data = await response.json();
                
                // Process the target URL to ensure it's in the correct format for React Router
                let processedUrl = data.target_url || targetUrl;
                
                if (processedUrl) {
                    // Remove any base URL or domain if present
                    if (processedUrl.includes(window.location.origin)) {
                        processedUrl = processedUrl.replace(window.location.origin, '');
                    }
                    
                    // Ensure the URL starts with '/' for React Router
                    if (!processedUrl.startsWith('/')) {
                        processedUrl = '/' + processedUrl;
                    }
                    
                    // Handle specific URL patterns that might cause issues
                    processedUrl = processedUrl.replace('/api/', '/');

                    // Navigate immediately after closing dropdown
                    navigate(processedUrl);
                } else {
                    // If no target URL, navigate to notifications page
                    navigate('/user/notifications');
                }
                
                // Remove the notification from the state (it's deleted)
                setNotifications((prevNotifications) =>
                    prevNotifications.filter((notification) => notification.id !== notificationId)
                );
                
                // Update the unread count (decrement by 1)
                setUnreadCount((prevCount) => Math.max(prevCount - 1, 0));
                
                // Broadcast event to update other components
                window.dispatchEvent(
                    new CustomEvent(NOTIFICATION_READ_EVENT, {
                        detail: { notificationId }
                    })
                );
            } else {
                console.error('Failed to delete notification');
            }
        } catch (error) {
            console.error('An error occurred while deleting the notification:', error);
        }
    };

    const getUserInitials = (firstName, lastName) => {
        return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
    };

    const getProfileImageUrl = (url) => {
        if (!url) return null;

        // If it's already a full URL (including S3), return it directly
        if (url.startsWith('http')) {
            return url;
        }

        // If it's an S3 URL without http(s), add https://
        if (url.includes('s3.amazonaws.com')) {
            return `https://${url.replace(/^https?:\/\//, '')}`;
        }

        // For local development
        if (url.startsWith('/')) {
            return `${config.backendUrl}${url}`;
        }

        return url;
    };

    const isActive = (path) => {
        if (typeof path === 'string') {
            // Decode the URL path to handle spaces and special characters
            const decodedPath = decodeURIComponent(location.pathname);
            const decodedComparePath = decodeURIComponent(path);
            return decodedPath === decodedComparePath;
        } else if (Array.isArray(path)) {
            return path.some(p => {
                const decodedPath = decodeURIComponent(location.pathname);
                const decodedComparePath = decodeURIComponent(p);
                return decodedPath.startsWith(decodedComparePath);
            });
        }
        return false;
    };

    const handleSideMenuToggle = () => {
        setIsSideMenuOpen(!isSideMenuOpen);
        setActiveCategory(null);
        setSubcategories([]);
    };

    const handleBackClick = () => {
        setActiveCategory(null);
    };

    const toggleCategoryDropdown = () => {
        setIsCategoryDropdownOpen(!isCategoryDropdownOpen);
        if (isProfileMenuOpen) setIsProfileMenuOpen(false);
        if (isNotificationDropdownOpen) setIsNotificationDropdownOpen(false);
    };

    const toggleProfileMenu = (event) => {
        // Prevent event bubbling to avoid conflicts
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        const newState = !isProfileMenuOpen;
        setIsProfileMenuOpen(newState);

        // Close other menus when opening profile menu
        if (newState) {
            setIsCategoryDropdownOpen(false);
            setIsNotificationDropdownOpen(false);
            setShowRecentSearches(false);
            setShowSuggestions(false);
        }
    };

    const toggleNotificationDropdown = async (event) => {
        // Prevent event bubbling to avoid conflicts
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        const newState = !isNotificationDropdownOpen;
        setIsNotificationDropdownOpen(newState);

        // Close other menus when opening notification dropdown
        if (newState) {
            setIsCategoryDropdownOpen(false);
            setIsProfileMenuOpen(false);
            setShowRecentSearches(false);
            setShowSuggestions(false);
            
            // Mark all notifications as read when opening dropdown
            try {
                const token = Cookies.get('authToken');
                if (token) {
                    const response = await fetch(`${config.backendUrl}/notifications/mark-all-read/`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Token ${token}`
                        }
                    });
                    
                    if (response.ok) {
                        const data = await response.json();
                        // Update unread count to 0
                        setUnreadCount(0);
                    }
                }
            } catch (error) {
                console.error('Error marking notifications as read:', error);
            }
        }
    };


    const handleNotificationClose = () => {
        setNotification({ message: '', type: '', header: '' });
    };

    const handleCategoryChange = (e) => {
        setSelectedCategory(e.target.value);
    };

    const fetchRecentSearches = async () => {
        if (!isLoggedIn) return;

        try {
            const token = Cookies.get('authToken');
            const response = await fetch(`${config.backendUrl}/products/user-search-history/`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Token ${token}`
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                setRecentSearches(data.searches || []);
            } else {
                setRecentSearches([]);
            }
        } catch (error) {
            console.error('Failed to fetch recent searches:', error);
            // Set empty array on error to prevent undefined errors
            setRecentSearches([]);
        }
    };

    // Debounced suggestion fetcher
    const fetchSuggestions = useCallback(async (value) => {
        setIsLoadingSuggestions(true);
        try {
            const response = await fetch(
                `${config.backendUrl}/products/search/suggestions/?q=${encodeURIComponent(value.trim())}`,
                {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include'
                }
            );
            if (response.ok) {
                const data = await response.json();
                setSuggestions(data.suggestions || []);
            } else {
                setSuggestions([]);
            }
        } catch (error) {
            console.error('Failed to fetch search suggestions:', error);
            setSuggestions([]);
        } finally {
            setIsLoadingSuggestions(false);
        }
    }, []);

    // Search handlers
    const handleSearchInputChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);
        setActiveSuggestionIndex(-1);
        
        // Close recent searches when typing
        setShowRecentSearches(false);
        
        // Clear existing debounce timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        
        if (value.trim().length >= 2) {
            setShowSuggestions(true);
            // Debounce API calls by 300ms
            debounceTimerRef.current = setTimeout(() => {
                fetchSuggestions(value);
            }, 300);
        } else {
            setShowSuggestions(false);
            setSuggestions([]);
            
            // Show recent searches if input is empty and user has search history
            if (value.trim().length === 0 && recentSearches.length > 0) {
                setShowRecentSearches(true);
            }
        }
    };

    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    // Keyboard navigation for search suggestions
    const handleSearchKeyDown = (e) => {
        const dropdownItems = document.querySelectorAll('.search-suggestions .suggestion-item, .recent-searches-dropdown .recent-search-item');
        const itemCount = dropdownItems.length;
        
        if (!itemCount && !showSuggestions && !showRecentSearches) return;
        
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveSuggestionIndex(prev => (prev + 1) % itemCount);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveSuggestionIndex(prev => (prev <= 0 ? itemCount - 1 : prev - 1));
        } else if (e.key === 'Enter' && activeSuggestionIndex >= 0 && dropdownItems[activeSuggestionIndex]) {
            e.preventDefault();
            dropdownItems[activeSuggestionIndex].click();
        } else if (e.key === 'Escape') {
            setShowSuggestions(false);
            setShowRecentSearches(false);
            setActiveSuggestionIndex(-1);
        }
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        
        if (!searchQuery.trim()) {
            setNotification({
                message: 'Veuillez saisir un terme de recherche',
                type: 'warning',
                header: 'Attention'
            });
            return;
        }

        // Close all dropdowns
        setShowSuggestions(false);
        setShowRecentSearches(false);
        closeAllMenus();

        // Save search to history for authenticated users
        if (isLoggedIn) {
            try {
                const token = Cookies.get('authToken');
                await fetch(`${config.backendUrl}/products/save-search-history/`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Token ${token}`
                    },
                    body: JSON.stringify({ keyword: searchQuery.trim() })
                });
                
                // Update local recent searches
                setRecentSearches(prev => {
                    const newSearch = { keyword: searchQuery.trim(), date: new Date().toISOString() };
                    const filtered = prev.filter(s => 
                        (typeof s === 'string' ? s : s.keyword) !== searchQuery.trim()
                    );
                    return [newSearch, ...filtered].slice(0, 10);
                });
            } catch (error) {
                console.error('Failed to save search history:', error);
            }
        }

        // Navigate to search results with timestamp to force refresh
        const searchParams = new URLSearchParams();
        searchParams.set('query', searchQuery.trim());
        if (selectedCategory && selectedCategory !== '') {
            searchParams.set('category', selectedCategory);
        }
        
        // Add timestamp parameter to force URL change and trigger re-search
        searchParams.set('t', Date.now().toString());
        
        navigate(`/search?${searchParams.toString()}`);
    };

    const handleClearSearch = () => {
        setSearchQuery('');
        setShowSuggestions(false);
        setShowRecentSearches(false);
        
        // Show recent searches if available
        if (recentSearches.length > 0) {
            setTimeout(() => setShowRecentSearches(true), 100);
        }
    };

    const handleSuggestionSelect = (suggestion) => {
        setSearchQuery(suggestion.text || suggestion);
        setShowSuggestions(false);
        setShowRecentSearches(false);
        
        // Trigger search with selected suggestion
        const searchParams = new URLSearchParams();
        searchParams.set('query', suggestion.text || suggestion);
        if (selectedCategory && selectedCategory !== '') {
            searchParams.set('category', selectedCategory);
        }
        navigate(`/search?${searchParams.toString()}`);
    };

    const closeAllMenus = () => {
        setIsCategoryDropdownOpen(false);
        setIsProfileMenuOpen(false);
        setIsNotificationDropdownOpen(false);
        setShowRecentSearches(false);
        setShowSuggestions(false);
    };
    const closeSideMenu = () => {
        setIsSideMenuOpen(false);
    }
    const ProfileImage = ({ user }) => {
        const [imageError, setImageError] = useState(false);

        if (!user) return null;

        if (imageError || !user.profile_picture_url) {
            return (
                <div className="navbar-initials" title={`${user.first_name} ${user.last_name}`}>
                    {getUserInitials(user.first_name, user.last_name)}
                </div>
            );
        }

        return (
            <img
                src={getProfileImageUrl(user.profile_picture_url)}
                alt={`${user.first_name} ${user.last_name}`}
                className="profile-picture-menu"
                onError={() => setImageError(true)}
            />
        );
    };

    useEffect(() => {
        if (isLoggedIn) {
            fetchRecentSearches();
        }
    }, [isLoggedIn]);

    // Enhanced keyboard accessibility
    useEffect(() => {
        const handleKeyDown = (event) => {
            // Close dropdowns on Escape key
            if (event.key === 'Escape') {
                closeAllMenus();
            }
            
            // Tab navigation support
            if (event.key === 'Tab' && (isProfileMenuOpen || isNotificationDropdownOpen)) {
                const activeDropdown = document.querySelector('.navbar-submenu.show');
                if (activeDropdown) {
                    const focusableElements = activeDropdown.querySelectorAll('a, button, input, [tabindex]:not([tabindex="-1"])');
                    const firstElement = focusableElements[0];
                    const lastElement = focusableElements[focusableElements.length - 1];
                    
                    if (event.shiftKey && document.activeElement === firstElement) {
                        event.preventDefault();
                        lastElement.focus();
                    } else if (!event.shiftKey && document.activeElement === lastElement) {
                        event.preventDefault();
                        firstElement.focus();
                    }
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isProfileMenuOpen, isNotificationDropdownOpen]);

    // Handle route changes to close menus
    useEffect(() => {
        // Close all menus when route changes
        closeAllMenus();
    }, [location.pathname]);

    return (
        <nav className="navbar">
            <div className="top-level-big-screen">
                <div className="logo">
                    <Link to="/">
                        <img src={logo} alt="Logo" className="navbar-logo" />
                    </Link>
                </div>
                <div className={`search-login ${isMenuOpen ? 'show' : ''}`}>
                        <form className="search-container" onSubmit={handleSearch} role="search">
                            <div className="category-menu">
                                <select
                                    className="category-dropdown"
                                    value={selectedCategory}
                                    onChange={handleCategoryChange}
                                    aria-label="Catégorie"
                                >
                                    <option key="" value="">Tout</option>
                                    {categoryList.map(category => (
                                        <option key={category.id} value={category.id}>
                                            {category.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="search-input-container">
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    name="search"
                                    placeholder="Rechercher..."
                                    className="search-box"
                                    value={searchQuery}
                                    onChange={handleSearchInputChange}
                                    onKeyDown={handleSearchKeyDown}
                                    onFocus={() => {
                                        if (searchQuery.trim()) {
                                            setShowSuggestions(true);
                                        } else if (recentSearches.length > 0) {
                                            setShowRecentSearches(true);
                                        }
                                    }}
                                    role="combobox"
                                    aria-autocomplete="list"
                                    aria-expanded={showSuggestions || showRecentSearches}
                                    aria-controls="search-suggestions-list"
                                    aria-activedescendant={activeSuggestionIndex >= 0 ? `suggestion-${activeSuggestionIndex}` : undefined}
                                    autoComplete="off"
                                />
                                {searchQuery && (
                                    <button
                                        type="button"
                                        className="search-input-clear-btn"
                                        onMouseDown={(e) => { e.preventDefault(); handleClearSearch(); }}
                                        aria-label="Clear search"
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                                )}
                                {showSuggestions && (suggestions.length > 0 || isLoadingSuggestions) && (
                                    <SearchSuggestions
                                        suggestions={suggestions}
                                        history={recentSearches}
                                        isLoading={isLoadingSuggestions}
                                        onSelect={handleSuggestionSelect}
                                        onClose={() => { setShowSuggestions(false); setActiveSuggestionIndex(-1); }}
                                        query={searchQuery}
                                        activeIndex={activeSuggestionIndex}
                                    />
                                )}
                                {showRecentSearches && !showSuggestions && recentSearches.length > 0 && (
                                    <div className="recent-searches-dropdown" id="search-suggestions-list" role="listbox">
                                        <div className="recent-searches-header">
                                            <span>Recherches récentes</span>
                                            <button
                                                onMouseDown={async (e) => {
                                                    e.preventDefault();
                                                    const token = Cookies.get('authToken');
                                                    try {
                                                        const response = await fetch(`${config.backendUrl}/users/search-history/`, {
                                                            method: 'DELETE',
                                                            headers: { 
                                                                'Authorization': `Token ${token}`,
                                                                'Content-Type': 'application/json'
                                                            }
                                                        });
                                                        if (response.ok) {
                                                            setRecentSearches([]);
                                                        }
                                                    } catch (error) {
                                                        console.error('Failed to clear search history:', error);
                                                    }
                                                }}
                                                className="clear-history-btn"
                                            >
                                                Effacer
                                            </button>
                                        </div>
                                        {recentSearches.map((search, index) => (
                                            <div
                                                key={index}
                                                id={`suggestion-${index}`}
                                                role="option"
                                                aria-selected={activeSuggestionIndex === index}
                                                className={`recent-search-item ${activeSuggestionIndex === index ? 'active' : ''}`}
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    const keyword = typeof search === 'string' ? search : search.keyword;
                                                    setSearchQuery(keyword);
                                                    navigate(`/search?category=${selectedCategory}&query=${keyword}`);
                                                    setShowRecentSearches(false);
                                                }}
                                            >
                                                <i className="fas fa-history"></i>
                                                <span>{typeof search === 'string' ? search : search.keyword}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <button type="submit" className="search-button" aria-label="Rechercher">
                                <i className="fas fa-search"></i>
                            </button>
                        </form>

                    {isLoggedIn ? (
                        <div className="logged-in-section">
                            <div className={`profile-menu ${isProfileMenuOpen ? 'active' : ''}`}>
                                <div 
                                    className='userDetails' 
                                    onClick={toggleProfileMenu}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            toggleProfileMenu(e);
                                        }
                                    }}
                                    tabIndex={0}
                                    role="button"
                                    aria-expanded={isProfileMenuOpen}
                                    aria-haspopup="true"
                                    aria-label="User menu"
                                >
                                    <div className='fullname-container'>
                                        <ProfileImage user={user} />
                                        {user ? (
                                            <span className="userNames">{userFirstName} {userLastName}</span>
                                        ) : (
                                            <span className="name-skeleton skeleton"></span>
                                        )}
                                    </div>
                                </div>
                                {isProfileMenuOpen && (
                                    <ul className="navbar-submenu show">
                                        <li className="navbar-submenu-header">
                                            <div className="user-header">
                                                <div className="user-header-details">
                                                    <span className="user-fullname">{user?.first_name} {user?.last_name}</span>
                                                    <span className="user-username">@{user?.username}</span>
                                                </div>
                                                <Link to={`/user/corner/${userUuid}/`} className="voir-plus-link" onClick={closeAllMenus}>Voir plus</Link>
                                            </div>
                                        </li>
                                        <hr className="navbar-submenu-separator" />
                                        
                                        <li className={`navbar-submenuItem ${isActive('/profile') ? 'active' : ''}`}>
                                            <Link to="/profile" className="navLink" onClick={closeAllMenus}>
                                                <div className="menu-item-content-wrapper">
                                                    <div className="icon-container" style={{ backgroundColor: 'rgba(255, 107, 107, 0.12)' }}>
                                                        <i className="fas fa-user icon-left" style={{ color: '#003366' }}></i>
                                                    </div>
                                                    <span className="menu-item-label">Profil</span>
                                                </div>
                                            </Link>
                                        </li>
                                        
                                        <li className={`navbar-submenuItem ${isActive(`/products/user/${userUuid}/`) ? 'active' : ''}`}>
                                            <Link to={`/products/user/${userUuid}/`} className="navLink" onClick={closeAllMenus}>
                                                <div className="menu-item-content-wrapper">
                                                    <div className="icon-container" style={{ backgroundColor: 'rgba(78, 205, 196, 0.12)' }}>
                                                        <i className="fas fa-box icon-left" style={{ color: '#003366' }}></i>
                                                    </div>
                                                    <span className="menu-item-label">Produits</span>
                                                    {userProducts > 0 && <span className="navbar-badge-count">{userProducts}</span>}
                                                </div>
                                            </Link>
                                        </li>
                                        
                                        <li className={`navbar-submenuItem ${isActive(`/user/likes`) ? 'active' : ''}`}>
                                            <Link to={`/user/likes/`} className="navLink" onClick={closeAllMenus}>
                                                <div className="menu-item-content-wrapper">
                                                    <div className="icon-container" style={{ backgroundColor: 'rgba(248, 196, 113, 0.12)' }}>
                                                        <i className="fas fa-heart icon-left" style={{ color: '#003366' }}></i>
                                                    </div>
                                                    <span className="menu-item-label">Favoris</span>
                                                </div>
                                            </Link>
                                        </li>
                                        
                                        <li className={`navbar-submenuItem ${isActive(`/user/corner/${userUuid}/`) ? 'active' : ''}`}>
                                            <Link to={`/user/corner/${userUuid}/`} className="navLink" onClick={closeAllMenus}>
                                                <div className="menu-item-content-wrapper">
                                                    <div className="icon-container" style={{ backgroundColor: 'rgba(130, 224, 170, 0.12)' }}>
                                                        <i className="fas fa-home icon-left" style={{ color: '#003366' }}></i>
                                                    </div>
                                                    <span className="menu-item-label">Boutique</span>
                                                </div>
                                            </Link>
                                        </li>
                                        
                                        <li className={`navbar-submenuItem ${isActive(`/messages/${userUuid}/`) ? 'active' : ''}`}>
                                            <Link to={`/messages/${userUuid}/`} className="navLink" onClick={closeAllMenus}>
                                                <div className="menu-item-content-wrapper">
                                                    <div className="icon-container" style={{ backgroundColor: 'rgba(187, 143, 206, 0.12)' }}>
                                                        <i className="fas fa-envelope icon-left" style={{ color: '#003366' }}></i>
                                                    </div>
                                                    <span className="menu-item-label">Chats</span>
                                                    {userNewMessages > 0 && <span className="navbar-badge-count navbar-badge-messages">{userNewMessages}</span>}
                                                </div>
                                            </Link>
                                        </li>
                                        
                                        <li className={`navbar-submenuItem ${isActive(`/portefeuille`) ? 'active' : ''}`}>
                                            <Link to={`/portefeuille`} className="navLink" onClick={closeAllMenus}>
                                                <div className="menu-item-content-wrapper">
                                                    <div className="icon-container" style={{ backgroundColor: 'rgba(133, 193, 233, 0.12)' }}>
                                                        <i className="fas fa-wallet icon-left" style={{ color: '#003366' }}></i>
                                                    </div>
                                                    <span className="menu-item-label">Mon Portefeuille</span>
                                                </div>
                                            </Link>
                                        </li>
                                        
                                        <li className={`navbar-submenuItem ${isActive(`/sms-history`) ? 'active' : ''}`}>
                                            <Link to={`/sms-history`} className="navLink" onClick={closeAllMenus}>
                                                <div className="menu-item-content-wrapper">
                                                    <div className="icon-container" style={{ backgroundColor: 'rgba(255, 165, 0, 0.12)' }}>
                                                        <i className="fas fa-sms icon-left" style={{ color: '#003366' }}></i>
                                                    </div>
                                                    <span className="menu-item-label">SMS aux clients</span>
                                                </div>
                                            </Link>
                                        </li>
                                        
                                        <li className={`navbar-submenuItem ${isActive('/business') ? 'active' : ''}`}>
                                            <Link to="/business" className="navLink" onClick={closeAllMenus}>
                                                <div className="menu-item-content-wrapper">
                                                    <div className="icon-container" style={{ backgroundColor: 'rgba(197, 22, 24, 0.12)' }}>
                                                        <i className="fas fa-rocket icon-left" style={{ color: '#c51618' }}></i>
                                                    </div>
                                                    <span className="menu-item-label">Wennze Business</span>
                                                </div>
                                            </Link>
                                        </li>
                                        
                                        <hr className="navbar-submenu-separator" />
                                        
                                        <li className={`navbar-submenuItem ${isActive('/settings') ? 'active' : ''}`}>
                                            <Link to="/settings" className="navLink" onClick={closeAllMenus}>
                                                <div className="menu-item-content-wrapper">
                                                    <div className="icon-container" style={{ backgroundColor: 'rgba(100, 116, 139, 0.12)' }}>
                                                        <i className="fas fa-cog icon-left" style={{ color: '#003366' }}></i>
                                                    </div>
                                                    <span className="menu-item-label">Paramètres</span>
                                                    <i className="fas fa-chevron-right" style={{ marginLeft: 'auto', fontSize: '12px', color: '#94a3b8' }}></i>
                                                </div>
                                            </Link>
                                        </li>
                                        
                                        <li className="navbar-submenuItem">
                                            <span
                                                className="navLink logout-link"
                                                onClick={() => { handleLogout(); closeAllMenus(); }}
                                            >
                                                {isLoggingOut ? (
                                                    <div className="menu-item-content-wrapper">
                                                        <div className="spinner"></div>
                                                        <span className="menu-item-label">Déconnexion en cours...</span>
                                                    </div>
                                                ) : (
                                                    <div className="menu-item-content-wrapper">
                                                        <div className="icon-container" style={{ backgroundColor: 'rgba(255, 234, 167, 0.12)' }}>
                                                            <i className="fas fa-sign-out-alt icon-left" style={{ color: '#c51618' }}></i>
                                                        </div>
                                                        <span className="menu-item-label" style={{ color: '#c51618' }}>Déconnexion</span>
                                                    </div>
                                                )}
                                            </span>
                                        </li>
                                    </ul>
                                )}
                            </div>
                            
                            <div 
                                className={`notification-bell navLink ${isNotificationDropdownOpen ? 'active' : ''}`} 
                                onClick={toggleNotificationDropdown}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        toggleNotificationDropdown(e);
                                    }
                                }}
                                tabIndex={0}
                                role="button"
                                aria-expanded={isNotificationDropdownOpen}
                                aria-haspopup="true"
                                aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
                            >
                                <i className="fas fa-bell big-icon"></i>
                                {unreadCount > 0 && (
                                    <span className="notification-count" aria-hidden="true">{unreadCount}</span>
                                )}
                                {isNotificationDropdownOpen && (
                                    <ul className="navbar-submenu show">
                                        <li className="navbar-submenu-header">
                                            <div className="notification-header">
                                                <span className="user-fullname">Notifications</span>
                                            </div>
                                        </li>
                                        <hr className="navbar-submenu-separator" />
                                        {notifications.length === 0 ? (
                                            <li className="navbar-submenuItem">Aucune notification</li>
                                        ) : (
                                            <>
                                                {notifications.slice(0, 5).map((notification) => (
                                                    <li
                                                        key={notification.id}
                                                        className={`navbar-submenuItem ${notification.is_read ? 'read' : 'unread'}`}
                                                    >
                                                        <Link
                                                            to="#"
                                                            className="navLink"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                handleNotificationClick(notification.id, notification.target_url);
                                                            }}
                                                        >
                                                            <span dangerouslySetInnerHTML={{ __html: notification.message }} />
                                                        </Link>
                                                    </li>
                                                ))}
                                                {notifications.length > 5 && (
                                                    <div className="navbar-voir">
                                                        <Link to="/user/notifications" className="voir-plus-link" onClick={closeNotificationDropdown}>
                                                            Voir tout
                                                        </Link>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </ul>
                                )}
                            </div>
                            {userId && (
                                <Link to="/users/discover" className="navLink">
                                    <i className="fas fa-compass big-icon-heart"></i>
                                </Link>
                            )}
                            <Link to="/add-post" className="add-post-button">
                                <i className="fas fa-plus"></i>
                                <span>Poster</span>
                            </Link>
                            <AppDownloadButton style={{ marginLeft: '8px' }} />
                        </div>
                    ) : (
                        <div className="auth-buttons-container">
                            <AppDownloadButton style={{ marginRight: '12px' }} />
                            <button
                                className="auth-button auth-button-login"
                                onClick={onLoginClick}
                            >
                                <i className="fas fa-sign-in-alt"></i> Connexion
                            </button>
                            <button
                                className="auth-button auth-button-register"
                                onClick={onRegisterClick}
                            >
                                <i className="fas fa-user-plus"></i> Inscription
                            </button>
                        </div>
                    )}
                </div>
            </div>
            <div className="top-level-small-screen">
                <div className="left-section">
                    <div className="hamburger-menu" onClick={handleSideMenuToggle}>
                        <div></div>
                        <div></div>
                        <div></div>
                    </div>
                    <div className="logo">
                        <Link to="/">
                            <img src={logo} alt="Logo" className="navbar-logo" />
                        </Link>
                    </div>
                </div>
                <div className="right-section">
                    {isLoggedIn ? (
                        <div className="logged-in-section">
                            <div className={`profile-menu ${isProfileMenuOpen ? 'active' : ''}`}>
                                <div 
                                    className='userDetails' 
                                    onClick={toggleProfileMenu}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            toggleProfileMenu(e);
                                        }
                                    }}
                                    tabIndex={0}
                                    role="button"
                                    aria-expanded={isProfileMenuOpen}
                                    aria-haspopup="true"
                                    aria-label="User menu"
                                >
                                    <ProfileImage user={user} />
                                </div>
                                {isProfileMenuOpen && (
                                    <ul className="navbar-submenu show">
                                        <li className="navbar-submenu-header">
                                            <div className="user-header">
                                                <div className="user-header-details">
                                                    {user ? (
                                                        <>
                                                            <span className="user-fullname">{userFirstName} {userLastName}</span>
                                                            <span className="user-username">@{userName}</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <span className="mobile-name-skeleton skeleton"></span>
                                                            <span className="mobile-name-skeleton skeleton"></span>
                                                        </>
                                                    )}
                                                </div>
                                                <Link to={`/user/corner/${userUuid}/`} className="voir-plus-link" onClick={closeAllMenus}>Voir plus</Link>
                                            </div>
                                        </li>
                                        <hr className="navbar-submenu-separator" />
                                        
                                        <li className={`navbar-submenuItem ${isActive('/profile') ? 'active' : ''}`}>
                                            <Link to="/profile" className="navLink" onClick={closeAllMenus}>
                                                <div className="menu-item-content-wrapper">
                                                    <div className="icon-container" style={{ backgroundColor: 'rgba(255, 107, 107, 0.12)' }}>
                                                        <i className="fas fa-user icon-left" style={{ color: '#003366' }}></i>
                                                    </div>
                                                    <span className="menu-item-label">Profil</span>
                                                </div>
                                            </Link>
                                        </li>
                                        
                                        <li className={`navbar-submenuItem ${isActive(`/products/user/${userUuid}/`) ? 'active' : ''}`}>
                                            <Link to={`/products/user/${userUuid}/`} className="navLink" onClick={closeAllMenus}>
                                                <div className="menu-item-content-wrapper">
                                                    <div className="icon-container" style={{ backgroundColor: 'rgba(78, 205, 196, 0.12)' }}>
                                                        <i className="fas fa-box icon-left" style={{ color: '#003366' }}></i>
                                                    </div>
                                                    <span className="menu-item-label">Produits</span>
                                                    {userProducts > 0 && <span className="navbar-badge-count">{userProducts}</span>}
                                                </div>
                                            </Link>
                                        </li>
                                        
                                        <li className={`navbar-submenuItem ${isActive(`/user/likes`) ? 'active' : ''}`}>
                                            <Link to={`/user/likes/`} className="navLink" onClick={closeAllMenus}>
                                                <div className="menu-item-content-wrapper">
                                                    <div className="icon-container" style={{ backgroundColor: 'rgba(248, 196, 113, 0.12)' }}>
                                                        <i className="fas fa-heart icon-left" style={{ color: '#003366' }}></i>
                                                    </div>
                                                    <span className="menu-item-label">Favoris</span>
                                                </div>
                                            </Link>
                                        </li>
                                        
                                        <li className={`navbar-submenuItem ${isActive(`/user/corner/${userUuid}/`) ? 'active' : ''}`}>
                                            <Link to={`/user/corner/${userUuid}/`} className="navLink" onClick={closeAllMenus}>
                                                <div className="menu-item-content-wrapper">
                                                    <div className="icon-container" style={{ backgroundColor: 'rgba(130, 224, 170, 0.12)' }}>
                                                        <i className="fas fa-home icon-left" style={{ color: '#003366' }}></i>
                                                    </div>
                                                    <span className="menu-item-label">Boutique</span>
                                                </div>
                                            </Link>
                                        </li>
                                        
                                        <li className={`navbar-submenuItem ${isActive(`/messages/${userUuid}/`) ? 'active' : ''}`}>
                                            <Link to={`/messages/${userUuid}/`} className="navLink" onClick={closeAllMenus}>
                                                <div className="menu-item-content-wrapper">
                                                    <div className="icon-container" style={{ backgroundColor: 'rgba(187, 143, 206, 0.12)' }}>
                                                        <i className="fas fa-envelope icon-left" style={{ color: '#003366' }}></i>
                                                    </div>
                                                    <span className="menu-item-label">Chats</span>
                                                    {userNewMessages > 0 && <span className="navbar-badge-count navbar-badge-messages">{userNewMessages}</span>}
                                                </div>
                                            </Link>
                                        </li>
                                        
                                        <li className={`navbar-submenuItem ${isActive(`/portefeuille`) ? 'active' : ''}`}>
                                            <Link to={`/portefeuille`} className="navLink" onClick={closeAllMenus}>
                                                <div className="menu-item-content-wrapper">
                                                    <div className="icon-container" style={{ backgroundColor: 'rgba(133, 193, 233, 0.12)' }}>
                                                        <i className="fas fa-wallet icon-left" style={{ color: '#003366' }}></i>
                                                    </div>
                                                    <span className="menu-item-label">Mon Portefeuille</span>
                                                </div>
                                            </Link>
                                        </li>
                                        
                                        <li className={`navbar-submenuItem ${isActive(`/sms-history`) ? 'active' : ''}`}>
                                            <Link to={`/sms-history`} className="navLink" onClick={closeAllMenus}>
                                                <div className="menu-item-content-wrapper">
                                                    <div className="icon-container" style={{ backgroundColor: 'rgba(255, 165, 0, 0.12)' }}>
                                                        <i className="fas fa-sms icon-left" style={{ color: '#003366' }}></i>
                                                    </div>
                                                    <span className="menu-item-label">SMS aux clients</span>
                                                </div>
                                            </Link>
                                        </li>
                                        
                                        <li className={`navbar-submenuItem ${isActive('/business') ? 'active' : ''}`}>
                                            <Link to="/business" className="navLink" onClick={closeAllMenus}>
                                                <div className="menu-item-content-wrapper">
                                                    <div className="icon-container" style={{ backgroundColor: 'rgba(197, 22, 24, 0.12)' }}>
                                                        <i className="fas fa-rocket icon-left" style={{ color: '#c51618' }}></i>
                                                    </div>
                                                    <span className="menu-item-label">Wennze Business</span>
                                                </div>
                                            </Link>
                                        </li>
                                        
                                        <hr className="navbar-submenu-separator" />
                                        
                                        <li className={`navbar-submenuItem ${isActive('/settings') ? 'active' : ''}`}>
                                            <Link to="/settings" className="navLink" onClick={closeAllMenus}>
                                                <div className="menu-item-content-wrapper">
                                                    <div className="icon-container" style={{ backgroundColor: 'rgba(100, 116, 139, 0.12)' }}>
                                                        <i className="fas fa-cog icon-left" style={{ color: '#003366' }}></i>
                                                    </div>
                                                    <span className="menu-item-label">Paramètres</span>
                                                    <i className="fas fa-chevron-right" style={{ marginLeft: 'auto', fontSize: '12px', color: '#94a3b8' }}></i>
                                                </div>
                                            </Link>
                                        </li>
                                        
                                        <li className="navbar-submenuItem">
                                            <span className="navLink logout-link" onClick={handleLogout}>
                                                {isLoggingOut ? (
                                                    <div className="menu-item-content-wrapper">
                                                        <div className="spinner"></div>
                                                        <span className="menu-item-label">Déconnexion en cours...</span>
                                                    </div>
                                                ) : (
                                                    <div className="menu-item-content-wrapper">
                                                        <div className="icon-container" style={{ backgroundColor: 'rgba(255, 234, 167, 0.12)' }}>
                                                            <i className="fas fa-sign-out-alt icon-left" style={{ color: '#c51618' }}></i>
                                                        </div>
                                                        <span className="menu-item-label" style={{ color: '#c51618' }}>Déconnexion</span>
                                                    </div>
                                                )}
                                            </span>
                                        </li>
                                    </ul>
                                )}
                            </div>
                            
                            <div className="notification-bell navLink" onClick={toggleNotificationDropdown}>
                                <i className="fas fa-bell big-icon"></i>
                                {unreadCount > 0 && (
                                    <span className="notification-count">{unreadCount}</span>
                                )}
                                {isNotificationDropdownOpen && (
                                    <ul className="navbar-submenu show">
                                        <li className="navbar-submenu-header">
                                            <div className="notification-header">
                                                <span className="user-fullname">Notifications</span>
                                            </div>
                                        </li>
                                        <hr className="navbar-submenu-separator" />
                                        {notifications.length === 0 ? (
                                            <li className="navbar-submenuItem">Aucune notification</li>
                                        ) : (
                                            <>
                                                {notifications.slice(0, 5).map(notification => (
                                                    <li key={notification.id} className="navbar-submenuItem">
                                                        <Link 
                                                            to="#" 
                                                            className="navLink" 
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                handleNotificationClick(notification.id, notification.target_url);
                                                            }}
                                                        >
                                                            <span dangerouslySetInnerHTML={{ __html: notification.message }} />
                                                        </Link>
                                                    </li>
                                                ))}
                                                {notifications.length > 5 && (
                                                    <div className="navbar-voir">
                                                        <Link to="/user/notifications" className="voir-plus-link" onClick={closeNotificationDropdown}>Voir tout</Link>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </ul>
                                )}
                            </div>
                            {userId && (
                                <Link to="/users/discover" className="navLink">
                                    <i className="fas fa-compass big-icon-heart"></i>
                                </Link>
                            )}
                            <Link to="/add-post" className="add-post-button">
                                <i className="fas fa-plus"></i>
                            </Link>
                        </div>
                    ) : (
                        <div className="auth-buttons-container">
                            <button
                                className="auth-button auth-button-login"
                                onClick={onLoginClick}
                            >
                                <i className="fas fa-sign-in-alt"></i> Connexion
                            </button>
                            <button
                                className="auth-button auth-button-register"
                                onClick={onRegisterClick}
                            >
                                <i className="fas fa-user-plus"></i> Inscription
                            </button>
                        </div>
                    )}
                </div>
            </div>
            <div className="second-level-small-screen">
                    <form className="search-container" onSubmit={handleSearch} role="search">
                        <div className="category-menu">
                            <select
                                className="category-dropdown"
                                value={selectedCategory}
                                onChange={handleCategoryChange}
                                aria-label="Catégorie"
                            >
                                <option key="" value="">Tout</option>
                                {categoryList.map(category => (
                                    <option key={category.id} value={category.id}>
                                        {category.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="search-input-container">
                            <input 
                                type="text" 
                                name="search" 
                                placeholder="Rechercher..." 
                                className="search-box"
                                value={searchQuery}
                                onChange={handleSearchInputChange}
                                onKeyDown={handleSearchKeyDown}
                                onFocus={() => {
                                    if (searchQuery.trim()) {
                                        setShowSuggestions(true);
                                    } else if (recentSearches.length > 0) {
                                        setShowRecentSearches(true);
                                    }
                                }}
                                role="combobox"
                                aria-autocomplete="list"
                                aria-expanded={showSuggestions || showRecentSearches}
                                aria-controls="search-suggestions-list-mobile"
                                aria-activedescendant={activeSuggestionIndex >= 0 ? `suggestion-mobile-${activeSuggestionIndex}` : undefined}
                                autoComplete="off"
                            />
                            {searchQuery && (                                    <button
                                        type="button"
                                        className="search-input-clear-btn"
                                        onMouseDown={(e) => { e.preventDefault(); handleClearSearch(); }}
                                        aria-label="Clear search"
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                            )}
                            {showSuggestions && (suggestions.length > 0 || isLoadingSuggestions) && (
                                <SearchSuggestions
                                    suggestions={suggestions}
                                    history={recentSearches}
                                    isLoading={isLoadingSuggestions}
                                    onSelect={handleSuggestionSelect}
                                    onClose={() => { setShowSuggestions(false); setActiveSuggestionIndex(-1); }}
                                    query={searchQuery}
                                    activeIndex={activeSuggestionIndex}
                                />
                            )}
                            {showRecentSearches && !showSuggestions && recentSearches.length > 0 && (
                                <div className="recent-searches-dropdown" id="search-suggestions-list-mobile" role="listbox">
                                    <div className="recent-searches-header">
                                        <span>Recherches récentes</span>
                                        <button
                                            onMouseDown={async (e) => {
                                                e.preventDefault();
                                                const token = Cookies.get('authToken');
                                                try {
                                                    const response = await fetch(`${config.backendUrl}/users/search-history/`, {
                                                        method: 'DELETE',
                                                        headers: { 
                                                            'Authorization': `Token ${token}`,
                                                            'Content-Type': 'application/json'
                                                        }
                                                    });
                                                    if (response.ok) {
                                                        setRecentSearches([]);
                                                    }
                                                } catch (error) {
                                                    console.error('Failed to clear search history:', error);
                                                }
                                            }}
                                            className="clear-history-btn"
                                        >
                                            Effacer
                                        </button>
                                    </div>
                                    {recentSearches.map((search, index) => (
                                        <div
                                            key={index}
                                            id={`suggestion-mobile-${index}`}
                                            role="option"
                                            aria-selected={activeSuggestionIndex === index}
                                            className={`recent-search-item ${activeSuggestionIndex === index ? 'active' : ''}`}
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                const keyword = typeof search === 'string' ? search : search.keyword;
                                                setSearchQuery(keyword);
                                                navigate(`/search?category=${selectedCategory}&query=${keyword}`);
                                                setShowRecentSearches(false);
                                            }}
                                        >
                                            <i className="fas fa-history"></i>
                                            <span>{typeof search === 'string' ? search : search.keyword}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <button type="submit" className="search-button" aria-label="Rechercher">
                            <i className="fas fa-search"></i>
                        </button>
                    </form>
            </div>

            <div className={`second-level ${isMenuOpen ? 'show' : ''}`}>
                <ul className="category-list">
                    <li className="category-item">
                        <span className="category-link" onClick={handleSideMenuToggle} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div className="hamburger-menu">
                                <div></div>
                                <div></div>
                                <div></div>
                            </div>
                        </span>
                    </li>
                    <li className="category-item">
                        <Link
                            to="/products/all"
                            className={`category-link ${isActive('/products/all') ? 'active' : ''}`}
                        >
                            <i className="fas fa-th"></i>
                            Voir Tous
                        </Link>
                    </li>
                    {topCategories.map(topCategory => (
                        <li
                            key={topCategory.id}
                            className={`category-item ${isActive(`/products/category/${topCategory.id}/${encodeURIComponent(topCategory.name)}`) ? 'active' : ''}`}
                        >
                            <Link
                                to={`/products/category/${topCategory.id}/${encodeURIComponent(topCategory.name)}`}
                                className={`category-link ${isActive(`/products/category/${topCategory.id}/${encodeURIComponent(topCategory.name)}`) ? 'active' : ''}`}
                            >
                                <i className={getCategoryIcon(topCategory.name)}></i>
                                {topCategory.name}
                            </Link>
                        </li>
                    ))}
                </ul>
            </div>
            {isSideMenuOpen && <div className="navbar-overlay show" onClick={handleSideMenuToggle}></div>}
            <div className={`side-menu ${isSideMenuOpen ? 'open' : ''}`}>
                <div className="side-menu-content">
                    <div className="side-menu-header">
                        <div className="logo">
                            <Link to="/">
                                <img src={logo} alt="Logo" className="navbar-logo" />
                            </Link>
                        </div>
                        <span className="nav-close-button" onClick={handleSideMenuToggle}>
                            <i className="fas fa-times"></i>
                        </span>
                        {activeCategory !== null && (
                            <span className="nav-back-button" onClick={handleBackClick}>
                                <i className="fas fa-chevron-left"></i>
                                <span>Retour au menu</span>
                            </span>
                        )}
                    </div>
                    <div className="scrollable-category-list">
                        {activeCategory === null ? (
                            <ul className="subcategory-list">
                                <Link
                                    to="/products/all"
                                    className={`category-link ${isActive('/products/all') ? 'active' : ''}`}
                                    onClick={closeSideMenu}
                                >
                                    <i className="fas fa-th"></i>
                                    Voir Tous
                                </Link>
                                {categories.map((category) => (
                                    <li
                                        key={category.id}
                                        className="category-item"
                                        onClick={() => handleCategoryClick(category.id)}
                                    >
                                        <span className="category-link">
                                            <i className={getCategoryIcon(category.name)}></i>
                                            {category.name}
                                        </span>
                                        <span className="category-arrow">
                                            <i className="fas fa-chevron-right"></i>
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <ul className="subcategory-list">
                                {subcategories && subcategories.length > 0 ? (
                                    subcategories.map((subcategory) => (
                                        <li key={subcategory.id} className="category-item">
                                            <Link
                                                to={`/products/category/${subcategory.id}/${encodeURIComponent(subcategory.name)}`}
                                                className="category-link"
                                                onClick={closeSideMenu}
                                            >
                                                <i className={getCategoryIcon(subcategory.name)}></i>
                                                {subcategory.name}
                                            </Link>
                                        </li>
                                    ))
                                ) : (
                                    <SideMenuCategorySkeleton />
                                )}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
            {notification.message && (
                <Notification
                    message={notification.message}
                    type={notification.type || 'info'} // Provide fallback
                    header={notification.header}
                    onClose={handleNotificationClose}
                />
            )}
            
            {/* Delete Account Warning Modal */}
            {showDeleteWarning && (
                <div className="modal-overlay" onClick={handleCancelDelete}>
                    <div className="modal-content delete-warning-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <i className="fas fa-exclamation-triangle" style={{ color: '#ff6b6b', fontSize: '48px' }}></i>
                        </div>
                        <h2 className="modal-title">Supprimer mon compte</h2>
                        <p className="modal-message">
                            Cette action est irréversible. Toutes vos données, produits et conversations seront définitivement supprimés.
                        </p>
                        <p className="modal-message" style={{ fontWeight: 'bold', marginTop: '10px' }}>
                            Êtes-vous sûr de vouloir continuer ?
                        </p>
                        <div className="modal-buttons">
                            <button className="modal-button cancel-button" onClick={handleCancelDelete}>
                                Annuler
                            </button>
                            <button className="modal-button confirm-button" onClick={handleConfirmDelete}>
                                Continuer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </nav>
    );
};

export default Navbar;